import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, XCircle } from '@phosphor-icons/react';

import { Spinner, Button } from '@/components/ui';
import { QuizScore } from '@/components/features/QuizScore/QuizScore';
import { useResponseDetail, useForm } from '@/api';
import { useAuthStore } from '@/stores';
import { ROUTES } from '@/constants';

import styles from './ResponseDetail.module.css';

const FIELD_TYPE_LABEL: Record<string, string> = {
    short_text: 'Texto corto',
    long_text: 'Texto largo',
    single_choice: 'Opcion unica',
    multiple_choice: 'Opcion multiple',
    dropdown: 'Desplegable',
    linear_scale: 'Escala lineal',
    date: 'Fecha',
    number: 'Numero',
    time: 'Hora',
    file_upload: 'Archivo',
    choice_grid: 'Grilla',
    checkbox_grid: 'Grilla multiple',
};

function formatValue(value: string, fieldType: string, moduleStyles: Record<string, string>): React.ReactNode {
    if (!value) return <span className={moduleStyles.emptyValue}>Sin respuesta</span>;

    if (fieldType === 'multiple_choice') {
        const choices = value.split('||').filter(Boolean);
        return (
            <ul className={moduleStyles.valueList}>
                {choices.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
        );
    }

    if (fieldType === 'choice_grid' || fieldType === 'checkbox_grid') {
        try {
            const parsed = JSON.parse(value) as Record<string, unknown>;
            return (
                <ul className={moduleStyles.valueList}>
                    {Object.entries(parsed).map(([row, val]) => (
                        <li key={row}>
                            <strong>{row}:</strong> {Array.isArray(val) ? val.join(', ') : String(val)}
                        </li>
                    ))}
                </ul>
            );
        } catch {
            return value;
        }
    }

    if (fieldType === 'file_upload') {
        return (
            <a href={value} target="_blank" rel="noopener noreferrer" className={moduleStyles.fileLink}>
                Ver archivo
            </a>
        );
    }

    return value;
}

export function ResponseDetail() {
    const { formSlug, responseId } = useParams<{
        userSlug: string;
        formSlug: string;
        responseId: string;
    }>();
    const navigate = useNavigate();
    const { getUser } = useAuthStore();
    const user = getUser();

    const { data: form, isLoading: formLoading } = useForm(formSlug || '');
    const { data: response, isLoading: responseLoading, error } = useResponseDetail(formSlug || '', responseId || '');

    useEffect(() => {
        if (form) {
            document.title = `Respuesta — ${form.title} — Ratio`;
        }
    }, [form]);

    const isOwner = form && user && form.author.id === user.id;
    const isRespondent = response?.respondent && user && response.respondent.id === user.id;

    if (formLoading || responseLoading) {
        return (
            <div className={styles.loading}>
                <Spinner size="lg" />
            </div>
        );
    }

    if (error || !response) {
        const status = (error as { response?: { status?: number } } | null)?.response?.status;
        if (status === 403) {
            return (
                <div className={styles.errorPage}>
                    <h2 className={styles.errorTitle}>Acceso restringido</h2>
                    <p className={styles.errorText}>No tienes permiso para ver esta respuesta.</p>
                    <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                        Volver al inicio
                    </Button>
                </div>
            );
        }
        return (
            <div className={styles.errorPage}>
                <h2 className={styles.errorTitle}>Respuesta no encontrada</h2>
                <Button variant="primary" onClick={() => navigate(-1)}>
                    Volver
                </Button>
            </div>
        );
    }

    const hasAccess = isOwner || isRespondent;
    if (!hasAccess) {
        return (
            <div className={styles.errorPage}>
                <h2 className={styles.errorTitle}>Acceso restringido</h2>
                <p className={styles.errorText}>Solo el autor o el respondente puede ver esta respuesta.</p>
                <Button variant="primary" onClick={() => navigate(ROUTES.feed)}>
                    Volver al inicio
                </Button>
            </div>
        );
    }

    const respondentName = response.respondent?.display_name ?? 'Anonimo';
    const submittedAt = new Date(response.created).toLocaleString('es', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });

    return (
        <div className={styles.page}>
            <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Volver">
                <ArrowLeft size={18} />
                Volver
            </button>

            <div className={styles.header}>
                <div>
                    <h1 className={styles.title}>Detalle de respuesta</h1>
                    {form && <p className={styles.formName}>{form.title}</p>}
                </div>
            </div>

            {/* Meta info */}
            <div className={styles.meta}>
                <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Respondente</span>
                    <span className={styles.metaValue}>{respondentName}</span>
                </div>
                <div className={styles.metaItem}>
                    <span className={styles.metaLabel}>Enviada</span>
                    <span className={styles.metaValue}>{submittedAt}</span>
                </div>
                {response.is_edited && (
                    <div className={styles.metaItem}>
                        <span className={styles.editedBadge}>Editada</span>
                    </div>
                )}
            </div>

            {/* Quiz score */}
            {response.quiz_score != null && response.quiz_total != null && (
                <QuizScore score={response.quiz_score} total={response.quiz_total} />
            )}

            {/* Answers */}
            <div className={styles.answers}>
                {response.answers.map((answer) => {
                    const allFields = form?.sections?.length
                        ? form.sections.flatMap((s) => s.fields)
                        : form?.fields ?? [];
                    const fieldDef = allFields.find((f) => f.id === answer.field_id);
                    const correctAnswer = fieldDef?.correct_answer;
                    const isQuiz = form?.mode === 'quiz';

                    let isCorrect: boolean | null = null;
                    if (isQuiz && correctAnswer && Object.keys(correctAnswer).length > 0) {
                        const correctValue = correctAnswer['value'] as string | undefined;
                        if (correctValue !== undefined) {
                            isCorrect = answer.value.trim().toLowerCase() === correctValue.trim().toLowerCase();
                        }
                    }

                    return (
                        <div key={answer.id} className={styles.answerCard}>
                            <div className={styles.answerHeader}>
                                <div className={styles.answerMeta}>
                                    <span className={styles.answerLabel}>{answer.field_label}</span>
                                    <span className={styles.answerType}>
                                        {FIELD_TYPE_LABEL[answer.field_type] ?? answer.field_type}
                                    </span>
                                </div>
                                {isCorrect !== null && (
                                    <span className={isCorrect ? styles.correct : styles.incorrect}>
                                        {isCorrect ? (
                                            <><CheckCircle size={14} weight="fill" /> Correcto</>
                                        ) : (
                                            <><XCircle size={14} weight="fill" /> Incorrecto</>
                                        )}
                                    </span>
                                )}
                            </div>
                            <div className={styles.answerValue}>
                                {answer.file_url ? (
                                    <a href={answer.file_url} target="_blank" rel="noopener noreferrer" className={styles.fileLink}>
                                        Ver archivo
                                    </a>
                                ) : (
                                    formatValue(answer.value, answer.field_type, styles)
                                )}
                            </div>
                            {isCorrect === false && fieldDef?.correct_answer && (
                                <div className={styles.correctAnswer}>
                                    Respuesta correcta: {String((fieldDef.correct_answer['value'] as string) ?? '')}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
